import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Winner {
  bowler_id?: number;
  bowler_ids?: number[];
  team_id?: number;
  bowler_name?: string;
  bowler_names?: string[];
  team_name?: string;
  rank: number;
  scratchScore?: number;
  handicapScore?: number;
  totalScore?: number;
}

interface SidePotPayoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  winners: Winner[];
  sideEventId: number;
  leagueId: number;
  defaultAmount: number;
  appliesTo: 'individual' | 'team' | 'doubles';
  onPayoutComplete: (winnerId: number, amount: number) => void;
}

const SidePotPayoutModal: React.FC<SidePotPayoutModalProps> = ({
  isOpen,
  onClose,
  winners,
  sideEventId,
  leagueId,
  defaultAmount,
  appliesTo,
  onPayoutComplete
}) => {
  const { t } = useTranslation();
  const [notes, setNotes] = useState('');
  const [payoutAmount, setPayoutAmount] = useState(defaultAmount.toString());
  const [isProcessing, setIsProcessing] = useState(false);
  const [paidWinners, setPaidWinners] = useState<Set<number>>(new Set());
  const [confirmations, setConfirmations] = useState<{ [key: number]: boolean }>({});

  if (!isOpen) return null;

  const handleRecordPayout = async (winner: Winner) => {
    if (isProcessing) return;

    const winnerId = appliesTo === 'team' ? winner.team_id! : winner.bowler_id!;
    const amount = parseFloat(payoutAmount);

    if (isNaN(amount) || amount <= 0) {
      alert(t('payoutModals.pleaseEnterValidAmount'));
      return;
    }

    setIsProcessing(true);

    try {
      if (appliesTo === 'doubles' && Array.isArray(winner.bowler_ids)) {
        // Pay both bowlers in the double
        for (const id of winner.bowler_ids) {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000/api'}/admin/payouts/sidepot`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              sideEventId,
              leagueId,
              recipientType: 'bowler',
              recipientId: id,
              amount,
              placement: winner.rank,
              notes: notes.trim()
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to record payout');
          }

          setPaidWinners(prev => new Set(prev).add(id));
          setConfirmations(prev => ({ ...prev, [id]: true }));
          onPayoutComplete(id, amount);

          setTimeout(() => {
            setConfirmations(prev => {
              const newConfirmations = { ...prev };
              delete newConfirmations[id];
              return newConfirmations;
            });
          }, 4000);
        }
      } else {
        // Bowler or team
        const winnerId = appliesTo === 'team' ? winner.team_id! : winner.bowler_id!;
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000/api'}/admin/payouts/sidepot`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            sideEventId,
            leagueId,
            recipientType: appliesTo,
            recipientId: winnerId,
            amount,
            placement: winner.rank,
            notes: notes.trim()
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to record payout');
        }

        setPaidWinners(prev => new Set(prev).add(winnerId));
        setConfirmations(prev => ({ ...prev, [winnerId]: true }));
        onPayoutComplete(winnerId, amount);

        setTimeout(() => {
          setConfirmations(prev => {
            const newConfirmations = { ...prev };
            delete newConfirmations[winnerId];
            return newConfirmations;
          });
        }, 4000);
      }
    } catch (error) {
      console.error('Error recording payout:', error);
      alert(t('payoutModals.payoutFailed', { error: error instanceof Error ? error.message : 'Unknown error' }));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
      // Reset modal state
      setNotes('');
      setPayoutAmount(defaultAmount.toString());
      setPaidWinners(new Set());
      setConfirmations({});
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">{t('payoutModals.sidePotPayout')}</h2>
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="text-gray-500 hover:text-gray-700 text-2xl disabled:opacity-50"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          {/* Payout Amount Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('payoutModals.payoutAmountCanBeModified')}
            </label>
            <div className="flex items-center space-x-2">
              <span className="text-gray-500">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('payoutModals.enterPayoutAmount')}
              />
            </div>
          </div>

          {/* Notes Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('payoutModals.notesOptional')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('payoutModals.addPayoutNotes')}
            />
          </div>

          {/* Winners List */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {t('payoutModals.sidePotWinners', { count: winners.length })}
            </h3>
            <div className="space-y-4">
              {winners.map((winner, index) => {
                const winnerName =
                  appliesTo === 'team'
                    ? winner.team_name!
                    : appliesTo === 'doubles'
                      ? (winner.bowler_names ? winner.bowler_names.join(' & ') : winner.bowler_ids?.join(' & '))
                      : winner.bowler_name!;
                const winnerId =
                  appliesTo === 'team'
                    ? winner.team_id
                    : appliesTo === 'doubles'
                      ? (winner.bowler_ids ? winner.bowler_ids.join(' & ') : '')
                      : winner.bowler_id;

                // For doubles, check if all bowler_ids are paid
                let isPaid = false;
                let showConfirmation = false;
                if (appliesTo === 'doubles' && Array.isArray(winner.bowler_ids)) {
                  isPaid = winner.bowler_ids.every(id => paidWinners.has(id));
                  showConfirmation = winner.bowler_ids.some(id => confirmations[id]);
                } else {
                  const singleId = appliesTo === 'team' ? winner.team_id! : winner.bowler_id!;
                  isPaid = paidWinners.has(singleId);
                  showConfirmation = confirmations[singleId];
                }

                return (
                  <div
                    key={index}
                    className={`border rounded-lg p-4 transition-all duration-300 ${isPaid ? 'bg-green-50 border-green-200 opacity-60' : 'bg-gray-50 border-gray-200'
                      }`}
                  >
                    {showConfirmation && (
                      <div className="mb-4 p-3 bg-green-100 border border-green-300 rounded-lg">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-green-800 font-medium">
                            {t('payoutModals.payoutRecordedFor', { name: winnerName })} {t('payoutModals.viewManageFinancialPage')}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                            #{winner.rank}
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-gray-800">
                              {winnerName}
                            </h4>
                            <div className="text-sm text-gray-600">
                              {t('payoutModals.id')}: {winnerId}
                            </div>
                            {winner.scratchScore && (
                              <div className="text-sm text-gray-600">
                                {t('payoutModals.score')}: {winner.scratchScore}
                                {winner.handicapScore && winner.handicapScore !== winner.scratchScore && (
                                  <span> ({t('payoutModals.withHandicap')}: {winner.handicapScore})</span>
                                )}
                              </div>
                            )}
                            {winner.totalScore && !winner.scratchScore && (
                              <div className="text-sm text-gray-600">
                                {t('payoutModals.totalScore')}: {winner.totalScore}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <div className="text-right">
                          <div className="text-sm text-gray-500">{t('payoutModals.payoutAmount')}</div>
                          <div className="text-lg font-bold text-green-600">
                            ${parseFloat(payoutAmount).toFixed(2)}
                          </div>
                        </div>

                        {!isPaid && (
                          <button
                            onClick={() => handleRecordPayout(winner)}
                            disabled={isProcessing || parseFloat(payoutAmount) <= 0 || isNaN(parseFloat(payoutAmount))}
                            className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition duration-200 disabled:cursor-not-allowed"
                          >
                            {isProcessing ? t('payoutModals.recording') : t('payoutModals.recordPayout')}
                          </button>
                        )}

                        {isPaid && (
                          <div className="bg-green-500 text-white px-4 py-2 rounded-lg">
                            ✓ {t('payoutModals.paid')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Modal Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              onClick={handleClose}
              disabled={isProcessing}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200 disabled:opacity-50"
            >
              {paidWinners.size === winners.length ? t('payoutModals.done') : t('payoutModals.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SidePotPayoutModal;